// Maps the appearance fields the distribution API exposes (via its
// `playerConfig`) onto the player's CSS custom properties.
//
// The CSS variable names deliberately differ from the config keys, and each
// value keeps the `var(--bam-player-*, var(--internal-*, <default>))` fallback
// chain so a merchant-facing `--bam-player-*` override still wins.
//
// Used by bam-player's `applyExternalAppearance` (`bamPlayer.js`) for the
// standalone SDK flow: there is no bam-bundle ancestor to write these vars on a
// wrapping `:host`, so the player sets them on its own host. The normal web flow
// keeps emitting the same vars inline in `bamBundle.js`.

// Defaults applied when a field is absent, matching the values bam-bundle uses.
const DEFAULT_THUMBNAIL_SIZE = '68px';
const DEFAULT_PLACEHOLDER_COLOR = '#EEE';
const DEFAULT_OVERLAY_FONT_WEIGHT = '700';
const DEFAULT_PLAYLIST_BACKGROUND_COLOR = '#000';

// var() fallback chains, hoisted to keep the mapping lines readable.
const EFFECTIVE_THUMBNAIL_SIZE = 'var(--bam-player-thumbnail-size, var(--remote-config-thumbnail-size, 68px))';
const AUTO_DETECTED_FONT_FAMILY = 'var(--bam-player-auto-detected-overlay-font-family, sans-serif)';

const CSS_PROPERTY_BY_APPEARANCE_FIELD = {
  thumbnailSize: 'width',
  playerOverlayFontFamily: 'font-family',
  playerOverlayFontWeight: 'font-weight',
  playerOverlayTitleFontSize: 'font-size',
  playerOverlayTitleLineHeight: 'line-height',
  playerPlaceholderColor: 'color',
  playerProductCardPadding: 'padding',
  playerProductCardBackgroundColor: 'color',
  playlistBackgroundColor: 'color',
};

// Product-card title-wrap expands to this trio of vars when enabled.
export const PRODUCT_CARD_TITLE_WRAP_PROPS = [
  '--internal-product-card-title-display',
  '--internal-product-card-title-white-space',
  '--internal-product-card-title-line-clamp',
];

// CSS properties that are only emitted when their field is present. bam-player
// clears any of these that are absent from the config so repeat calls stay
// idempotent (a field dropped from a later call reverts to its CSS fallback).
export const CONDITIONAL_APPEARANCE_CSS_PROPS = [
  '--internal-overlay-title-font-size',
  '--internal-overlay-title-line-height',
  '--internal-product-card-padding',
  '--internal-product-card-background-color',
  ...PRODUCT_CARD_TITLE_WRAP_PROPS,
];

// Card padding is a free-text field; a bare number (e.g. "0") is not a valid
// CSS length, which breaks the product-info edge-inset math. Give it a unit.
// Emit the trimmed value so surrounding whitespace (e.g. " 6 ") doesn't produce
// an invalid " 6 px".
const normalizeProductCardPadding = (padding) => {
  const trimmed = String(padding).trim();
  return /^-?\d*\.?\d+$/.test(trimmed) ? `${trimmed}px` : trimmed;
};

// An empty custom property is still "declared", so a blank value poisons the
// `var(--x, fallback)` chains downstream. Treat blank/null/undefined as absent,
// mirroring the omitBlankValues() used when appearances are composed.
const isPresent = (value) => value !== undefined && value !== null && value !== '';

/**
 * Parse an external SDK payload into the appearance fields the player accepts.
 * CSS.supports provides the platform parser for each field's actual CSS type;
 * invalid and unknown fields are omitted so they resolve through the defaults.
 */
export const getSafeAppearanceConfig = (appearance, supports) => {
  if (!appearance || typeof appearance !== 'object' || Array.isArray(appearance)) return {};

  const cssEntries = Object.entries(CSS_PROPERTY_BY_APPEARANCE_FIELD).flatMap(([field, property]) => {
    const value = appearance[field];
    if (typeof value !== 'string' || !value.trim()) return [];

    const normalizedValue = field === 'playerProductCardPadding'
      ? normalizeProductCardPadding(value)
      : value.trim();
    return supports(property, normalizedValue) ? [[field, normalizedValue]] : [];
  });
  const titleWrapEntry = typeof appearance.playerProductCardTitleWrap === 'boolean'
    ? [['playerProductCardTitleWrap', appearance.playerProductCardTitleWrap]]
    : [];

  return Object.fromEntries([...cssEntries, ...titleWrapEntry]);
};

/**
 * Map an appearance config (a subset of the distribution `playerConfig`) to an
 * ordered array of `[cssPropertyName, value]` pairs. Optional fields that are
 * absent are skipped rather than emitted as `undefined`.
 */
export const getAppearanceCssVars = (config = {}) => {
  const {
    thumbnailSize,
    playerOverlayFontFamily,
    playerOverlayFontWeight,
    playerOverlayTitleFontSize,
    playerOverlayTitleLineHeight,
    playerPlaceholderColor,
    playerProductCardPadding,
    playerProductCardBackgroundColor,
    playerProductCardTitleWrap,
    playlistBackgroundColor,
  } = config || {};

  const fontFamily = playerOverlayFontFamily ?? AUTO_DETECTED_FONT_FAMILY;
  const backgroundColor = playlistBackgroundColor ?? DEFAULT_PLAYLIST_BACKGROUND_COLOR;

  const vars = [
    ['--remote-config-thumbnail-size', thumbnailSize ?? DEFAULT_THUMBNAIL_SIZE],
    ['--effective-thumbnail-size', EFFECTIVE_THUMBNAIL_SIZE],
    ['--internal-font-family', `var(--bam-player-overlay-font-family, ${fontFamily})`],
    ['--internal-overlay-font-weight', playerOverlayFontWeight ?? DEFAULT_OVERLAY_FONT_WEIGHT],
    ['--internal-placeholder-color', playerPlaceholderColor ?? DEFAULT_PLACEHOLDER_COLOR],
    ['--internal-background-color', `var(--bam-playlist-background-color, ${backgroundColor})`],
  ];

  // These carry no historical default; emit only when provided.
  if (isPresent(playerOverlayTitleFontSize)) {
    vars.push(['--internal-overlay-title-font-size', playerOverlayTitleFontSize]);
  }
  if (isPresent(playerOverlayTitleLineHeight)) {
    vars.push(['--internal-overlay-title-line-height', playerOverlayTitleLineHeight]);
  }
  if (isPresent(playerProductCardPadding)) {
    vars.push(['--internal-product-card-padding', normalizeProductCardPadding(playerProductCardPadding)]);
  }
  if (isPresent(playerProductCardBackgroundColor)) {
    vars.push(['--internal-product-card-background-color', playerProductCardBackgroundColor]);
  }
  if (playerProductCardTitleWrap) {
    vars.push(
      ['--internal-product-card-title-display', '-webkit-box'],
      ['--internal-product-card-title-white-space', 'normal'],
      ['--internal-product-card-title-line-clamp', '2'],
    );
  }

  return vars;
};
